import { CreditCardIcon } from '@phosphor-icons/react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React, { useState } from 'react'

import { useBuyShopItem } from '@/api/shop/buy-shop-item'
import { useSignToPaidTraining } from '@/api/training/sign-to-paid-training'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { getApiErrorMessage } from '@/utils/error'

import * as S from './stripe-payment-modal.styled'
import { StripePaymentModalProps } from './stripe-payment-modal.types'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

type PaymentFormProps = {
  context: StripePaymentModalProps['context']
  onPaymentSuccess?: () => void
  onPaymentError?: () => void
  onClose: () => void
}

const PaymentForm = ({ context, onPaymentSuccess, onPaymentError, onClose }: PaymentFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { mutateAsync: signToPaidTraining } = useSignToPaidTraining()
  const { mutateAsync: buyShopItem } = useBuyShopItem()
  const [error, setError] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setIsPending(true)
    setError(null)
    try {
      const cardElement = elements.getElement(CardElement)
      const { token, error: tokenError } = await stripe.createToken(cardElement!)
      if (tokenError || !token) {
        setError(tokenError?.message ?? 'Failed to create payment token.')
        return
      }
      if ('trainingId' in context) {
        await signToPaidTraining({ cardToken: token.id, trainingId: context.trainingId })
      } else {
        await buyShopItem({
          cardToken: token.id,
          skuId: context.skuId,
          ...(context.recipient && { recipient: context.recipient }),
          ...(context.giftMessage && { message: context.giftMessage }),
          ...(context.giftCard && { giftCard: context.giftCard }),
          ...(context.promoCode && { promoCode: context.promoCode }),
        })
      }
      onPaymentSuccess?.()
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err))
      onPaymentError?.()
    } finally {
      setIsPending(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <S.CardWrapper>
        <CardElement />
      </S.CardWrapper>
      {error && <S.ErrorWrapper>{error}</S.ErrorWrapper>}
      <S.SubmitWrapper>
        <Button loading={isPending} type="submit" disabled={!stripe}>
          Pay Now
        </Button>
      </S.SubmitWrapper>
    </form>
  )
}

export const StripePaymentModal = ({
  onClose,
  context,
  onPaymentSuccess,
  onPaymentError,
}: StripePaymentModalProps) => {
  return (
    <Modal title="Payment" headerIcon={CreditCardIcon} size="medium" onClose={onClose}>
      <Elements stripe={stripePromise}>
        <PaymentForm
          context={context}
          onPaymentSuccess={onPaymentSuccess}
          onPaymentError={onPaymentError}
          onClose={onClose}
        />
      </Elements>
    </Modal>
  )
}
